'use client';
import { useState, useCallback, useEffect } from 'react';
import { Compass, RefreshCw, CheckCircle, Download, Target, Grid, Sparkles, FolderOpen, FileText, HardDrive, AlertTriangle, Search } from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';
import { AgentPageLayout, AgentInputPanel, AgentLabel, AgentStreamOutput, AgentEmptyState } from '@/components/AgentPageLayout';
import { PROJECT_TYPES, AGENT_CONFIGS, downloadAsTextFile } from '@/lib/agent-constants';
import { AgentToolbar } from '@/components/AgentToolbar';

interface ProgramRoom { id: string; name: string; designArea: number; requiredArea: number; floor: string; ceiling: string; ceilingH: number; acType: string; remarks: string; status: 'ok' | 'warn' | 'error'; }
interface NasFile { name: string; path: string; size?: number; }
interface NasProject { id: string; name: string; yearGroup: string; path: string; fileCount: number; }

export default function DesignAgentPage() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const config = AGENT_CONFIGS.design;
    const [rooms, setRooms] = useState<ProgramRoom[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [aiStream, setAiStream] = useState('');
    const [aiDone, setAiDone] = useState(false);
    const [projectType, setProjectType] = useState('학교');
    const [projectName, setProjectName] = useState('');
    const [totalArea, setTotalArea] = useState('');
    const [floors, setFloors] = useState('');

    // NAS 연동 상태
    const [mode, setMode] = useState<'manual' | 'nas'>('nas');
    const [nasProjects, setNasProjects] = useState<NasProject[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [nasFiles, setNasFiles] = useState<NasFile[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
    const [loadingNas, setLoadingNas] = useState(false);
    const [projectSearch, setProjectSearch] = useState('');

    // NAS 프로젝트 목록 로드
    useEffect(() => {
        fetch('/api/nas/projects')
            .then(r => r.json())
            .then(data => {
                if (data.connected && data.projects) {
                    setNasProjects(data.projects);
                    // 기본 선택
                    const ptk = data.projects.find((p: any) => p.id.includes('A프로젝트'));
                    if (ptk) setSelectedProjectId(ptk.id);
                }
            })
            .catch(() => {});
    }, []);

    // 프로젝트 선택 시 파일 로드
    useEffect(() => {
        if (!selectedProjectId || mode !== 'nas') return;
        setLoadingNas(true);
        setNasFiles([]);
        setSelectedFiles([]);

        fetch(`/api/ai-tools/analyze?projectId=${encodeURIComponent(selectedProjectId)}&agent=design`)
            .then(r => r.json())
            .then(data => {
                if (data.success) {
                    setNasFiles(data.files || []);
                    // 자동 선택 (최대 5개)
                    const autoSelect = (data.files || [])
                        .slice(0, 5)
                        .map((f: any) => f.name);
                    setSelectedFiles(autoSelect);
                }
            })
            .catch(() => {})
            .finally(() => setLoadingNas(false));
    }, [selectedProjectId, mode]);

    const toggleFile = (name: string) => {
        setSelectedFiles(prev =>
            prev.includes(name) ? prev.filter(f => f !== name) : [...prev, name]
        );
    };

    // NAS 기반 분석
    const analyzeFromNas = useCallback(async () => {
        if (!selectedProjectId) return;
        setIsProcessing(true);
        setAiStream('');
        setAiDone(false);
        setRooms([]);

        try {
            const res = await fetch('/api/ai-tools/analyze', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    agent: 'design',
                    projectId: selectedProjectId,
                    files: selectedFiles.length > 0 ? selectedFiles : undefined,
                    query: `프로젝트 유형: ${projectType}, 프로젝트명: ${projectName || selectedProjectId}`,
                }),
            });

            if (!res.ok) throw new Error('API 오류');
            const reader = res.body?.getReader();
            if (!reader) throw new Error('스트림 없음');
            const decoder = new TextDecoder();
            let fullText = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                for (const line of chunk.split('\n')) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.substring(6));
                            if (data.type === 'chunk') { fullText += data.text; setAiStream(fullText); }
                            else if (data.type === 'complete') { fullText = data.fullText; setAiStream(fullText); }
                        } catch { }
                    }
                }
            }

            parseRoomsFromText(fullText);
            setAiDone(true);
        } catch (err: any) { setAiStream(`오류: ${err.message}`); setAiDone(true); }
        setIsProcessing(false);
    }, [selectedProjectId, selectedFiles, projectType, projectName]);

    // 수동 분석
    const loadProgramTable = useCallback(async () => {
        setIsProcessing(true);
        setAiStream('');
        setAiDone(false);
        setRooms([]);

        try {
            const res = await fetch('/api/design-agent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    projectType,
                    projectName: projectName || `${projectType} 신축 프로젝트`,
                    totalArea: totalArea || '5000',
                    floors: floors || '3',
                    analysisType: '설계 프로그램표 생성 + 면적/마감 검증'
                }),
            });

            if (!res.ok) throw new Error('API 오류');
            const reader = res.body?.getReader();
            if (!reader) throw new Error('스트림 없음');
            const decoder = new TextDecoder();
            let fullText = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                for (const line of chunk.split('\n')) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.substring(6));
                            if (data.type === 'chunk') { fullText += data.text; setAiStream(fullText); }
                            else if (data.type === 'complete') { fullText = data.fullText; setAiStream(fullText); }
                        } catch { }
                    }
                }
            }
            parseRoomsFromText(fullText);
            setAiDone(true);
        } catch (err: any) { setAiStream(`오류: ${err.message}`); setAiDone(true); }
        setIsProcessing(false);
    }, [projectType, projectName, totalArea, floors]);

    function parseRoomsFromText(text: string) {
        try {
            const jsonMatch = text.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                const parsed = JSON.parse(jsonMatch[0]);
                if (parsed.rooms && Array.isArray(parsed.rooms)) {
                    setRooms(parsed.rooms.map((r: any, idx: number) => ({
                        id: r.id || String(idx + 1), name: r.name || '', designArea: r.designArea || 0, requiredArea: r.requiredArea || 0,
                        floor: r.floor || '', ceiling: r.ceiling || '', ceilingH: r.ceilingH || 2.7, acType: r.acType || '', remarks: r.remarks || '', status: r.status || 'ok'
                    })));
                }
            }
        } catch { }
    }

    const totalDesign = rooms.reduce((a, r) => a + r.designArea, 0);
    const totalRequired = rooms.reduce((a, r) => a + r.requiredArea, 0);
    const okCount = rooms.filter(r => r.status === 'ok').length;
    const errCount = rooms.filter(r => r.status === 'error').length;

    const handleExport = () => {
        if (!aiStream) return;
        const filename = `DESIGN_프로그램표_${projectName || selectedProjectId || projectType}_${new Date().toISOString().slice(0, 10)}.txt`;
        downloadAsTextFile(aiStream, filename);
    };

    const filteredProjects = projectSearch
        ? nasProjects.filter(p => p.id.toLowerCase().includes(projectSearch.toLowerCase()) || p.name.toLowerCase().includes(projectSearch.toLowerCase()))
        : nasProjects;

    return (
        <AgentPageLayout config={config} icon={<Compass className="w-5 h-5" />} isProcessing={isProcessing} aiDone={aiDone}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-4">
                    <AgentInputPanel>
                        {/* 모드 전환 */}
                        <div className="flex gap-1.5 mb-3">
                            <button onClick={() => setMode('nas')}
                                className={`flex-1 py-2 rounded-lg text-[11px] font-bold cursor-pointer transition flex items-center justify-center gap-1 ${mode === 'nas' ? `${config.accentBg} ${config.accentText} border ${config.accentBorder}` : isDark ? 'bg-white/5 text-gray-500 border border-white/10' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>
                                <HardDrive className="w-3 h-3" /> NAS 프로젝트
                            </button>
                            <button onClick={() => setMode('manual')}
                                className={`flex-1 py-2 rounded-lg text-[11px] font-bold cursor-pointer transition flex items-center justify-center gap-1 ${mode === 'manual' ? `${config.accentBg} ${config.accentText} border ${config.accentBorder}` : isDark ? 'bg-white/5 text-gray-500 border border-white/10' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>
                                <FileText className="w-3 h-3" /> 수동 입력
                            </button>
                        </div>

                        {mode === 'nas' ? (
                            <>
                                {/* NAS 프로젝트 선택 */}
                                <div>
                                    <AgentLabel>NAS 프로젝트 선택</AgentLabel>
                                    <div className="relative mt-1.5">
                                        <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-500" />
                                        <input value={projectSearch} onChange={e => setProjectSearch(e.target.value)} placeholder="프로젝트 검색..."
                                            className={`w-full pl-8 pr-3 py-2 rounded-lg text-xs ${isDark ? 'bg-white/5 border border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'}`} />
                                    </div>
                                    <select value={selectedProjectId} onChange={e => setSelectedProjectId(e.target.value)}
                                        className={`w-full mt-1.5 px-3 py-2.5 rounded-lg text-xs ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-gray-50 border border-gray-200 text-gray-900'}`}>
                                        <option value="">프로젝트 선택...</option>
                                        {filteredProjects.slice(0, 50).map(p => (
                                            <option key={p.id} value={p.id}>{p.id} ({p.fileCount}파일)</option>
                                        ))}
                                    </select>
                                    <div className={`text-[10px] mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                        {nasProjects.length > 0 ? `${nasProjects.length}개 프로젝트 감지됨` : 'NAS 연결 중...'}
                                    </div>
                                </div>

                                {/* 파일 목록 */}
                                {nasFiles.length > 0 && (
                                    <div>
                                        <AgentLabel>분석할 파일 ({selectedFiles.length}/{nasFiles.length})</AgentLabel>
                                        <div className={`mt-1.5 max-h-[200px] overflow-y-auto rounded-lg border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                                            {nasFiles.map(f => (
                                                <label key={f.name} className={`flex items-center gap-2 px-2.5 py-1.5 text-[10px] cursor-pointer hover:bg-white/5 transition ${isDark ? 'border-b border-white/5' : 'border-b border-gray-100'}`}>
                                                    <input type="checkbox" checked={selectedFiles.includes(f.name)} onChange={() => toggleFile(f.name)}
                                                        className="w-3 h-3 rounded accent-teal-500" />
                                                    <span className={`truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{f.name}</span>
                                                    {f.size && <span className={`text-[9px] flex-shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{(f.size / 1024).toFixed(0)}KB</span>}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {loadingNas && (
                                    <div className="text-center py-3">
                                        <RefreshCw className={`w-4 h-4 animate-spin mx-auto ${isDark ? 'text-teal-400' : 'text-teal-500'}`} />
                                        <div className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>NAS 파일 조회 중...</div>
                                    </div>
                                )}

                                {/* 추가 옵션 */}
                                <div>
                                    <AgentLabel>건물 용도</AgentLabel>
                                    <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                                        {PROJECT_TYPES.map(t => (
                                            <button key={t} onClick={() => setProjectType(t)}
                                                className={`py-1.5 rounded-lg text-[10px] font-bold cursor-pointer transition ${projectType === t ? `${config.accentBg} ${config.accentText} border ${config.accentBorder}` : isDark ? 'bg-white/5 text-gray-500 border border-white/10' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>{t}</button>
                                        ))}
                                    </div>
                                </div>

                                <button onClick={analyzeFromNas} disabled={isProcessing || !selectedProjectId}
                                    className={`w-full py-3 rounded-xl bg-gradient-to-r ${config.gradient} text-white text-xs font-bold cursor-pointer disabled:opacity-40 flex items-center justify-center gap-2 shadow-lg`}>
                                    {isProcessing ? <><RefreshCw className="w-4 h-4 animate-spin" />NAS 파일 분석 중...</> : <><FolderOpen className="w-4 h-4" />NAS 프로젝트 분석 시작</>}
                                </button>
                            </>
                        ) : (
                            /* 기존 수동 입력 모드 */
                            <>
                                <div>
                                    <AgentLabel>건물 용도</AgentLabel>
                                    <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                                        {PROJECT_TYPES.map(t => (
                                            <button key={t} onClick={() => setProjectType(t)}
                                                className={`py-2 rounded-lg text-[10px] font-bold cursor-pointer transition ${projectType === t ? `${config.accentBg} ${config.accentText} border ${config.accentBorder}` : isDark ? 'bg-white/5 text-gray-500 border border-white/10' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>{t}</button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <AgentLabel>프로젝트명</AgentLabel>
                                    <input value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="예: 웅촌초등학교"
                                        className={`w-full mt-1.5 px-3 py-2.5 rounded-lg text-xs ${isDark ? 'bg-white/5 border border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'}`} />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div><AgentLabel>연면적 (㎡)</AgentLabel><input value={totalArea} onChange={e => setTotalArea(e.target.value)} placeholder="5000" type="number" className={`w-full mt-1.5 px-3 py-2.5 rounded-lg text-xs ${isDark ? 'bg-white/5 border border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'}`} /></div>
                                    <div><AgentLabel>층수</AgentLabel><input value={floors} onChange={e => setFloors(e.target.value)} placeholder="3" type="number" className={`w-full mt-1.5 px-3 py-2.5 rounded-lg text-xs ${isDark ? 'bg-white/5 border border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'}`} /></div>
                                </div>
                                <button onClick={loadProgramTable} disabled={isProcessing}
                                    className={`w-full py-3 rounded-xl bg-gradient-to-r ${config.gradient} text-white text-xs font-bold cursor-pointer disabled:opacity-40 flex items-center justify-center gap-2 shadow-lg`}>
                                    {isProcessing ? <><RefreshCw className="w-4 h-4 animate-spin" />AI 분석 중...</> : <><Sparkles className="w-4 h-4" />AI 프로그램표 생성</>}
                                </button>
                            </>
                        )}
                    </AgentInputPanel>
                </div>
                <div className="lg:col-span-8 space-y-4">
                    {rooms.length > 0 && (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                                {[
                                    { label: '총 실 수', value: rooms.length, unit: '개', color: 'text-teal-400' },
                                    { label: '설계 면적', value: totalDesign.toFixed(1), unit: '㎡', color: 'text-emerald-400' },
                                    { label: '요구 면적', value: totalRequired.toFixed(1), unit: '㎡', color: 'text-cyan-400' },
                                    { label: '적합', value: okCount, unit: `/ ${rooms.length}`, color: 'text-emerald-400' },
                                    { label: '부족', value: errCount, unit: '건', color: 'text-red-400' },
                                ].map((s, i) => (
                                    <div key={i} className={`rounded-xl border p-3 ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                                        <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</div>
                                        <div className={`text-lg font-bold ${s.color}`}>{s.value} <span className="text-[10px] font-normal">{s.unit}</span></div>
                                    </div>
                                ))}
                            </div>
                            <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                                <div className={`px-4 py-2.5 border-b flex items-center justify-between ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                                    <span className="text-xs font-bold flex items-center gap-2"><Grid className="w-4 h-4 text-teal-400" /> 설계 프로그램표 {selectedProjectId && `(${selectedProjectId})`}</span>
                                    <button onClick={handleExport} className={`text-[10px] px-3 py-1.5 rounded-lg cursor-pointer flex items-center gap-1 font-bold transition hover:opacity-80 ${isDark ? 'bg-teal-500/20 text-teal-300' : 'bg-teal-50 text-teal-600'}`}><Download className="w-3 h-3" />내보내기</button>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-[10px]">
                                        <thead><tr className={isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}>
                                            {['상태', '실명', '설계면적', '요구면적', '차이', '바닥', '천장', '천장고', '공조', '비고'].map(h => (<th key={h} className={`text-left px-3 py-2.5 font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>))}
                                        </tr></thead>
                                        <tbody>
                                            {rooms.map(r => {
                                                const diff = r.designArea - r.requiredArea;
                                                return (
                                                    <tr key={r.id} className={`border-t ${isDark ? 'border-white/5 hover:bg-white/[0.03]' : 'border-gray-100 hover:bg-gray-50'} transition`}>
                                                        <td className="px-3 py-2.5">{r.status === 'ok' ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : r.status === 'warn' ? <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> : <Target className="w-3.5 h-3.5 text-red-400" />}</td>
                                                        <td className="px-3 py-2.5 font-semibold">{r.name}</td>
                                                        <td className="px-3 py-2.5 font-mono text-right">{r.designArea.toFixed(1)}</td>
                                                        <td className={`px-3 py-2.5 font-mono text-right ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{r.requiredArea.toFixed(1)}</td>
                                                        <td className={`px-3 py-2.5 font-mono text-right font-bold ${diff >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{diff >= 0 ? '+' : ''}{diff.toFixed(1)}</td>
                                                        <td className={`px-3 py-2.5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>{r.floor}</td>
                                                        <td className={`px-3 py-2.5 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>{r.ceiling}</td>
                                                        <td className="px-3 py-2.5 font-mono text-right">{r.ceilingH}</td>
                                                        <td className="px-3 py-2.5">{r.acType}</td>
                                                        <td className={`px-3 py-2.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{r.remarks}</td>
                                                    </tr>
                                                );
                                            })}
                                            <tr className={`border-t-2 font-bold ${isDark ? 'border-teal-500/30 bg-teal-500/5' : 'border-teal-300 bg-teal-50'}`}>
                                                <td className="px-3 py-2.5"></td>
                                                <td className="px-3 py-2.5 text-teal-400">합계 ({rooms.length}실)</td>
                                                <td className="px-3 py-2.5 text-right font-mono text-teal-400">{totalDesign.toFixed(1)}</td>
                                                <td className="px-3 py-2.5 text-right font-mono">{totalRequired.toFixed(1)}</td>
                                                <td className={`px-3 py-2.5 text-right font-mono ${totalDesign - totalRequired >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{totalDesign - totalRequired >= 0 ? '+' : ''}{(totalDesign - totalRequired).toFixed(1)}</td>
                                                <td colSpan={5}></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                    <AgentStreamOutput aiStream={aiStream} isProcessing={isProcessing} accentColor="text-teal-400" />
                    {aiStream && !isProcessing && (
                        <AgentToolbar title={`설계 검토 — ${selectedProjectId || projectName}`} content={aiStream} agentType="design" projectId={selectedProjectId} projectName={selectedProjectId || projectName} />
                    )}
                    {!aiStream && !isProcessing && rooms.length === 0 && (
                        <AgentEmptyState
                            icon={<Compass className={`w-8 h-8 ${isDark ? 'text-teal-500/30' : 'text-teal-200'}`} />}
                            message={mode === 'nas' ? 'NAS 프로젝트를 선택하고 분석을 시작하세요 — 실제 파일을 AI가 직접 분석합니다' : '프로젝트 정보를 입력하고 AI 프로그램표 생성을 시작하세요'}
                        />
                    )}
                </div>
            </div>
        </AgentPageLayout>
    );
}
